// Libs
import classNames from 'classnames/bind';
import { Grid2, IconButton, Typography } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import { BaseInput, BaseSelect } from '~/components/common';
// Others
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
  REG_EXP,
} from '~/utils/constants/common';
import { ISelectMaterial, IQueryParamMaterial } from '~/utils/interface/material';
import { formatCurrency, formatInputDecimal } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getMaterialList } from '~/thunks/material/materialThunk';
import useDebounce from '~/utils/hooks/useDebounce';
import { IFormEstimate } from '~/utils/interface/estimate';
// Styles, images, icons
import styles from './EstimateMaterials.module.scss';
import { icons } from '~/assets';

type Props = {
  index: number;
  onRemoveItem: (index: number) => void;
};

const cx = classNames.bind(styles);

const EstimateMaterials = (props: Props) => {
  //#region Destructuring Props
  const { index, onRemoveItem } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/job']);

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<IFormEstimate>();
  const dispatch = useAppDispatch();

  const quantity = useWatch({ control, name: `projectedMaterials.${index}.quantity` });
  const price = useWatch({ control, name: `projectedMaterials.${index}.price` });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [optionMaterials, setOptionMaterials] = useState<ISelectMaterial[]>([]);
  const [materialsSearchKey, setMaterialsSearchKey] = useState<string>(EMPTY_STRING);
  const debounceMaterialsSearchKey = useDebounce(materialsSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [isLoadingMaterials, setIsLoadingMaterials] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  const total = useMemo(() => {
    return String(Number(price) * Number(quantity));
  }, [price, quantity]);
  //#endregion Declare Memo

  useEffect(() => {
    setValue(`projectedMaterials.${index}.totalPrice`, total, {
      shouldDirty: true,
    });
  }, [total, index, setValue]);

  const handleGetMaterialsList = useCallback(
    (params: IQueryParamMaterial) => {
      setIsLoadingMaterials(true);
      dispatch(getMaterialList(params))
        .unwrap()
        .then((res) => {
          const { responses } = res?.data;

          if (!responses) return;

          const dataMaterials: ISelectMaterial[] = responses.map((item) => ({
            value: item?.id,
            label: item?.name || EMPTY_STRING,
            price: item?.price || EMPTY_STRING,
            cost: item?.cost || EMPTY_STRING,
            quantity: item?.quantity || EMPTY_STRING,
            description: item?.description || EMPTY_STRING,
          }));
          setOptionMaterials(dataMaterials);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoadingMaterials(false);
        });
    },
    [dispatch]
  );

  //#region Implement Hook
  useEffect(() => {
    const params: IQueryParamMaterial = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      ...(debounceMaterialsSearchKey && { textSearch: debounceMaterialsSearchKey }),
    };

    handleGetMaterialsList(params);
  }, [debounceMaterialsSearchKey, handleGetMaterialsList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleOnchangeMaterial = (value: ISelectMaterial) => {
    setValue(`projectedMaterials.${index}.materialId`, value?.value || EMPTY_STRING, { shouldDirty: true });
    setValue(`projectedMaterials.${index}.price`, value?.price || EMPTY_STRING, { shouldDirty: true });
    setValue(`projectedMaterials.${index}.cost`, value?.cost || EMPTY_STRING, { shouldDirty: true });
    setValue(`projectedMaterials.${index}.quantity`, value?.quantity || EMPTY_STRING, { shouldDirty: true });
    setValue(`projectedMaterials.${index}.description`, value?.description || EMPTY_STRING, {
      shouldDirty: true,
    });
  };

  const handleSearchMaterialsChange = (searchKey: string) => {
    setMaterialsSearchKey(searchKey);
  };
  //#endregion Handle Function

  return (
    <Grid2
      component='div'
      id='estimateMaterialsComponent'
      className={cx('container')}
      container
      rowSpacing={'12px'}
      columnSpacing={'12px'}
    >
      <Grid2
        size={'grow'}
        container
        rowSpacing={'12px'}
        columnSpacing={'12px'}
        className={cx(index !== DEFAULT_NUMBER_ZERO ? 'item' : EMPTY_STRING)}
      >
        <Grid2 container size={12}>
          <Grid2 size={'grow'}>
            <Controller
              name={`projectedMaterials.${index}.materialId`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseSelect
                  label={t('field_material_name_label')}
                  value={value || EMPTY_STRING}
                  onChange={(optionValue) => handleOnchangeMaterial(optionValue)}
                  options={optionMaterials || []}
                  errorMessage={errors?.projectedMaterials?.[index]?.materialId?.message}
                  required
                  onSearchChange={handleSearchMaterialsChange}
                  isLoading={isLoadingMaterials}
                />
              )}
            />
          </Grid2>

          <Grid2 size={'grow'}>
            <Controller
              name={`projectedMaterials.${index}.quantity`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('field_material_quantity_label')}
                  value={value}
                  required
                  onChange={(event) => {
                    const newValue = formatInputDecimal(event.target.value);
                    setValue(`projectedMaterials.${index}.quantity`, newValue, { shouldDirty: true });
                  }}
                  errorMessage={errors?.projectedMaterials?.[index]?.quantity?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={'grow'}>
            <Controller
              name={`projectedMaterials.${index}.price`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('field_material_price_label')}
                  value={value}
                  required
                  onChange={(event) => {
                    const inputValue = event.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                    const number = inputValue ? Number(inputValue).toString() : EMPTY_STRING;
                    setValue(`projectedMaterials.${index}.price`, number, { shouldDirty: true });
                  }}
                  errorMessage={errors?.projectedMaterials?.[index]?.price?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={'grow'}>
            <Controller
              name={`projectedMaterials.${index}.cost`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('field_material_cost_label')}
                  value={value}
                  required
                  onChange={(event) => {
                    const inputValue = event.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                    const number = inputValue ? Number(inputValue).toString() : EMPTY_STRING;
                    setValue(`projectedMaterials.${index}.cost`, number, { shouldDirty: true });
                  }}
                  errorMessage={errors?.projectedMaterials?.[index]?.cost?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 className={cx('totalField')} width={'auto'}>
            <Typography className={cx('textTotal')}>{formatCurrency(total)}</Typography>

            <Grid2 size='auto' className='flex justify-center items-center'>
              <IconButton className={cx('closeField')} onClick={() => onRemoveItem(index)}>
                <img src={icons.commonIconClose} alt={t('common_alt_img', { ns: 'common/base' })} />
              </IconButton>
            </Grid2>
          </Grid2>
        </Grid2>

        <Grid2 container size={12}>
          <Grid2 size={12}>
            <Controller
              name={`projectedMaterials.${index}.description`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('field_material_description_label')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.projectedMaterials?.[index]?.description?.message}
                />
              )}
            />
          </Grid2>
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

export default memo(EstimateMaterials);
